"use client";

/**
 * useSmartAccountBalances
 *
 * Queries live on-chain token balances for a Hoops smart account address.
 * Uses Soroban simulation (no signing required) — the same pattern as
 * HoopsPasskeyKit.queryBalance() but without needing a kit instance.
 *
 * Refreshes automatically every 30 seconds.
 */

import { useEffect, useRef, useCallback, useReducer } from "react";
import {
  rpc,
  TransactionBuilder,
  Account,
  Address,
  Contract,
  xdr,
} from "@stellar/stellar-sdk";
import {
  HOOPS_RPC_URL,
  HOOPS_NETWORK_PASSPHRASE,
  TESTNET_TOKENS,
  POOL_ADDRESSES,
  TOKEN_UNIT,
} from "@/lib/hoops-contracts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SmartAccountBalances {
  /** USDC balance, formatted in human units (e.g. 12.34) */
  usdc: number;
  /** XLM balance, formatted in human units */
  xlm: number;
  /** Aqua LP share balance, formatted in human units */
  aquaLp: number;
  /** Soroswap LP balance, formatted in human units (Soroswap pair IS the LP token) */
  soroswapLp: number;
  /** XLM price in USD, derived from Soroswap USDC/XLM pool reserves */
  xlmPrice: number;
  /** Estimated USD value of Aqua LP position */
  aquaLpValue: number;
  /** Estimated USD value of Soroswap LP position */
  soroswapLpValue: number;
}

export interface UseSmartAccountBalancesResult {
  balances: SmartAccountBalances;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REFRESH_INTERVAL_MS = 30_000;

/**
 * Dummy null account used as the TX source for simulations.
 * Simulation never submits, so sequence number doesn't matter.
 */
const NULL_ACCOUNT = "GAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHF";

const ZERO_BALANCES: SmartAccountBalances = {
  usdc: 0,
  xlm: 0,
  aquaLp: 0,
  soroswapLp: 0,
  xlmPrice: 0,
  aquaLpValue: 0,
  soroswapLpValue: 0,
};

// ---------------------------------------------------------------------------
// Low-level balance query (simulation, no auth required)
// ---------------------------------------------------------------------------

/** Parse an i128 ScVal back to a bigint. Matches the kit's scValToI128. */
function scValToI128(val: xdr.ScVal): bigint {
  try {
    const i128 = val.i128();
    const hi = BigInt(i128.hi().toString());
    const lo = BigInt(i128.lo().toString());
    return (hi << 64n) | lo;
  } catch {
    return 0n;
  }
}

/** Convert raw stroops (bigint, 7 decimals) to a human-readable number. */
function stroopsToHuman(raw: bigint): number {
  // Use Number safely: balances in testnet won't overflow float64
  return Number(raw) / TOKEN_UNIT;
}

/**
 * Simulate a token contract's `balance(address)` call and return the raw bigint.
 * Returns 0n on any error (RPC down, account not found, zero balance, etc.).
 */
async function queryTokenBalance(
  server: rpc.Server,
  tokenContractId: string,
  accountAddress: string,
): Promise<bigint> {
  try {
    const tokenContract = new Contract(tokenContractId);
    const sourceAccount = new Account(NULL_ACCOUNT, "0");

    const tx = new TransactionBuilder(sourceAccount, {
      fee: "100",
      networkPassphrase: HOOPS_NETWORK_PASSPHRASE,
    })
      .addOperation(
        tokenContract.call("balance", new Address(accountAddress).toScVal()),
      )
      .setTimeout(30)
      .build();

    const sim = await server.simulateTransaction(tx);

    if ("result" in sim && sim.result) {
      const retval = (sim.result as { retval?: xdr.ScVal }).retval;
      if (retval) return scValToI128(retval);
    }

    return 0n;
  } catch {
    // Silently return 0 — RPC errors or missing accounts are expected
    // when a smart account hasn't been funded/used yet.
    return 0n;
  }
}

// ---------------------------------------------------------------------------
// Pool reserve + total supply queries (for LP valuation)
// ---------------------------------------------------------------------------

interface PoolReserves {
  reserveUsdc: bigint;
  reserveXlm: bigint;
}

/**
 * Query Soroswap pair reserves via `get_reserves()`.
 * Returns { reserveUsdc (token0), reserveXlm (token1) }.
 */
async function querySoroswapReserves(
  server: rpc.Server,
): Promise<PoolReserves | null> {
  try {
    const pairContract = new Contract(POOL_ADDRESSES.SOROSWAP_PAIR);
    const sourceAccount = new Account(NULL_ACCOUNT, "0");

    const tx = new TransactionBuilder(sourceAccount, {
      fee: "100",
      networkPassphrase: HOOPS_NETWORK_PASSPHRASE,
    })
      .addOperation(pairContract.call("get_reserves"))
      .setTimeout(30)
      .build();

    const sim = await server.simulateTransaction(tx);
    if ("result" in sim && sim.result) {
      const retval = (sim.result as { retval?: xdr.ScVal }).retval;
      if (retval) {
        const vec = retval.vec();
        if (vec && vec.length >= 2) {
          return {
            reserveUsdc: scValToI128(vec[0]),
            reserveXlm: scValToI128(vec[1]),
          };
        }
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Query a token contract's `total_supply()` — works for both Soroswap pair
 * tokens and Aqua LP tokens (SAC-compatible interface).
 */
async function queryTotalSupply(
  server: rpc.Server,
  tokenContractId: string,
): Promise<bigint> {
  try {
    const tokenContract = new Contract(tokenContractId);
    const sourceAccount = new Account(NULL_ACCOUNT, "0");

    const tx = new TransactionBuilder(sourceAccount, {
      fee: "100",
      networkPassphrase: HOOPS_NETWORK_PASSPHRASE,
    })
      .addOperation(tokenContract.call("total_supply"))
      .setTimeout(30)
      .build();

    const sim = await server.simulateTransaction(tx);
    if ("result" in sim && sim.result) {
      const retval = (sim.result as { retval?: xdr.ScVal }).retval;
      if (retval) return scValToI128(retval);
    }
    return 0n;
  } catch {
    return 0n;
  }
}

/**
 * Query Aqua pool reserves by reading USDC and XLM balances held by the pool contract.
 * Aqua pools hold tokens directly — their reserve is the token balance at the pool address.
 */
async function queryAquaPoolReserves(
  server: rpc.Server,
): Promise<PoolReserves | null> {
  try {
    const [reserveUsdc, reserveXlm] = await Promise.all([
      queryTokenBalance(server, TESTNET_TOKENS.USDC, POOL_ADDRESSES.AQUA_POOL),
      queryTokenBalance(server, TESTNET_TOKENS.XLM, POOL_ADDRESSES.AQUA_POOL),
    ]);
    if (reserveUsdc === 0n && reserveXlm === 0n) return null;
    return { reserveUsdc, reserveXlm };
  } catch {
    return null;
  }
}

/**
 * Calculate the USD value of an LP position given:
 * - the user's LP token balance
 * - pool reserves (USDC + XLM)
 * - total LP supply
 * - XLM price in USD
 *
 * Value = (userLp / totalSupply) * (reserveUsdc + reserveXlm * xlmPrice)
 * All inputs in raw stroops (bigint). Returns human-unit USD number.
 */
function valueLpPosition(
  userLp: bigint,
  totalSupply: bigint,
  reserves: PoolReserves,
  xlmPrice: number,
): number {
  if (totalSupply === 0n || userLp === 0n) return 0;

  const reserveUsdcHuman = Number(reserves.reserveUsdc) / TOKEN_UNIT;
  const reserveXlmHuman = Number(reserves.reserveXlm) / TOKEN_UNIT;
  const totalPoolValueUsd = reserveUsdcHuman + reserveXlmHuman * xlmPrice;
  const shareRatio = Number(userLp) / Number(totalSupply);

  return shareRatio * totalPoolValueUsd;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

interface State {
  balances: SmartAccountBalances;
  loading: boolean;
  error: string | null;
}

type Action =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; balances: SmartAccountBalances }
  | { type: "FETCH_ERROR"; error: string };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { balances: action.balances, loading: false, error: null };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSmartAccountBalances(
  smartAccountAddress: string | undefined,
): UseSmartAccountBalancesResult {
  const [state, dispatch] = useReducer(reducer, {
    balances: ZERO_BALANCES,
    loading: false,
    error: null,
  });

  // Stable server instance (created once per hook mount)
  const serverRef = useRef<rpc.Server | null>(null);
  if (!serverRef.current) {
    serverRef.current = new rpc.Server(HOOPS_RPC_URL);
  }

  // Track whether the component is still mounted to avoid state updates on unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchBalances = useCallback(async () => {
    if (!smartAccountAddress) return;

    dispatch({ type: "FETCH_START" });

    try {
      const server = serverRef.current;
      if (!server) return;

      // Fire all balance + pool queries in parallel
      const [
        usdcRaw,
        xlmRaw,
        aquaLpRaw,
        soroswapLpRaw,
        soroswapReserves,
        aquaReserves,
        soroswapTotalSupply,
        aquaLpTotalSupply,
      ] = await Promise.all([
        queryTokenBalance(server, TESTNET_TOKENS.USDC, smartAccountAddress),
        queryTokenBalance(server, TESTNET_TOKENS.XLM, smartAccountAddress),
        queryTokenBalance(server, POOL_ADDRESSES.AQUA_LP_TOKEN, smartAccountAddress),
        queryTokenBalance(server, POOL_ADDRESSES.SOROSWAP_PAIR, smartAccountAddress),
        querySoroswapReserves(server),
        queryAquaPoolReserves(server),
        queryTotalSupply(server, POOL_ADDRESSES.SOROSWAP_PAIR),
        queryTotalSupply(server, POOL_ADDRESSES.AQUA_LP_TOKEN),
      ]);

      if (!isMountedRef.current) return;

      // Derive XLM price from Soroswap USDC/XLM pool reserves
      let xlmPrice = 0;
      if (soroswapReserves && soroswapReserves.reserveXlm > 0n) {
        xlmPrice =
          Number(soroswapReserves.reserveUsdc) /
          Number(soroswapReserves.reserveXlm);
      }

      // Value LP positions
      const soroswapLpValue =
        soroswapReserves && soroswapTotalSupply > 0n
          ? valueLpPosition(soroswapLpRaw, soroswapTotalSupply, soroswapReserves, xlmPrice)
          : 0;

      const aquaLpValue =
        aquaReserves && aquaLpTotalSupply > 0n
          ? valueLpPosition(aquaLpRaw, aquaLpTotalSupply, aquaReserves, xlmPrice)
          : 0;

      dispatch({
        type: "FETCH_SUCCESS",
        balances: {
          usdc: stroopsToHuman(usdcRaw),
          xlm: stroopsToHuman(xlmRaw),
          aquaLp: stroopsToHuman(aquaLpRaw),
          soroswapLp: stroopsToHuman(soroswapLpRaw),
          xlmPrice,
          aquaLpValue,
          soroswapLpValue,
        },
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      const message =
        err instanceof Error ? err.message : "Failed to fetch balances";
      dispatch({ type: "FETCH_ERROR", error: message });
    }
  }, [smartAccountAddress]);

  // Fetch on mount and whenever the address changes
  useEffect(() => {
    if (!smartAccountAddress) return;
    void fetchBalances();
  }, [smartAccountAddress, fetchBalances]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!smartAccountAddress) return;
    const timer = setInterval(() => {
      void fetchBalances();
    }, REFRESH_INTERVAL_MS);
    return () => { clearInterval(timer); };
  }, [smartAccountAddress, fetchBalances]);

  return {
    balances: state.balances,
    loading: state.loading,
    error: state.error,
    refresh: () => { void fetchBalances(); },
  };
}

// ---------------------------------------------------------------------------
// Derived helpers (exported for use in display components)
// ---------------------------------------------------------------------------

/** Format a human-unit balance as a USD string (2 decimal places). */
export function formatUSDBalance(value: number): string {
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Format a token balance with up to 4 significant decimal places. */
export function formatTokenBalance(value: number): string {
  if (value === 0) return "0.00";
  if (value < 0.0001) return "< 0.0001";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/** Return the count of LP positions that hold a non-zero balance. */
export function countActiveLpPositions(balances: SmartAccountBalances): number {
  return [balances.aquaLp, balances.soroswapLp].filter((b) => b > 0).length;
}

